// V1.25.949 — PacksPreviewBlock : aperçu des packs sur la page d'accueil.
//
// Affiche les packs publics (props `packs` injectee par le rendu serveur de
// la home) en cartes stylees avec :
//   - Nom du pack + badge optionnel (POPULAIRE)
//   - Prix mensuel + annuel (toggle)
//   - 3-5 features cles (extra_details du pack ou config.highlights)
//   - Bouton "Choisir ce pack"
//   - Lien global "Voir tous les details" → /tarifs
//
// Source de verite UNIQUE : les packs en base (super-admin → Packs).
// Le user n'a rien a configurer dans le widget — il choisit juste quels
// packs afficher (par defaut tous les packs active_on_site=true).
//
// Structure config :
// {
//   title: 'Choisissez votre formule',
//   subtitle: 'Sans engagement, changez quand vous voulez.',
//   show_billing_toggle: true,
//   default_billing: 'yearly',
//   max_features_per_pack: 5,        // nb d'extra_details a afficher
//   pack_ids: [],                    // vide = tous les packs ; sinon liste UUIDs
//   see_more_text: 'Voir tous les details',
//   see_more_url: '/tarifs',
// }
'use client';

import { useState } from 'react';
import * as Icons from 'lucide-react';
import { Check, ArrowRight } from 'lucide-react';

// V1.25.1027 — Mapping aligné avec PacksWidget.jsx (page /tarifs).
// Le icon picker du super-admin peut stocker l'icône sous 3 formes
// différentes selon les versions :
//   1) clé technique : "sparkles", "star", "rocket"...
//   2) nom Lucide direct : "Sparkles", "Star", "Wand2"...
//   3) label FR (legacy / ancien picker) : "Étoiles IA", "Couronne", "Fusée"...
// resolveIcon() couvre les 3 cas pour ne JAMAIS afficher une icône fausse.

const ICON_BY_KEY = {
  // IA & Premium
  sparkles: Icons.Sparkles, star: Icons.Star, crown: Icons.Crown, gem: Icons.Gem,
  rocket: Icons.Rocket, flame: Icons.Flame, zap: Icons.Zap, award: Icons.Award,
  trophy: Icons.Trophy, wand: Icons.Wand2,
  // IA
  brain: Icons.Brain, bot: Icons.Bot, cpu: Icons.Cpu,
  // Securite
  shield: Icons.Shield, lock: Icons.Lock, key: Icons.Key, badge: Icons.BadgeCheck,
  // Business
  briefcase: Icons.Briefcase, file: Icons.FileText, receipt: Icons.Receipt,
  clipboard: Icons.ClipboardList, folder: Icons.Folder,
  // Equipe
  users: Icons.Users, user: Icons.User, usercheck: Icons.UserCheck,
  // Support
  phone: Icons.Phone, mail: Icons.Mail, message: Icons.MessageCircle,
  headphones: Icons.Headphones, lifebuoy: Icons.LifeBuoy,
  // Temps
  calendar: Icons.Calendar, clock: Icons.Clock, bell: Icons.Bell,
  // Analytics
  trendup: Icons.TrendingUp, barchart: Icons.BarChart3,
  piechart: Icons.PieChart, linechart: Icons.LineChart,
  // Web
  globe: Icons.Globe, cloud: Icons.Cloud, wifi: Icons.Wifi,
  // Finance
  card: Icons.CreditCard, wallet: Icons.Wallet, euro: Icons.BadgeEuro,
  // Produit
  package: Icons.Package, gift: Icons.Gift, tag: Icons.Tag, truck: Icons.Truck,
  // Reglages
  settings: Icons.Settings, wrench: Icons.Wrench, cog: Icons.Cog,
  // Validation
  check: Icons.CheckCircle2, thumbsup: Icons.ThumbsUp, heart: Icons.Heart,
  // Medias
  camera: Icons.Camera, image: Icons.Image, video: Icons.Video,
  // Lieux
  building: Icons.Building2, home: Icons.Home,
  // Misc
  eye: Icons.Eye,
};

// Mapping label FR (label visible dans le picker) → key technique
const FR_LABEL_TO_KEY = {
  // IA / Premium
  'etoilesia':            'sparkles',
  'etoiles':              'sparkles',
  'etoile':               'star',
  'couronne':             'crown',
  'diamant':              'gem',
  'fusee':                'rocket',
  'flamme':               'flame',
  'eclair':               'zap',
  'medaille':             'award',
  'trophee':              'trophy',
  'baguettemagique':      'wand',
  // IA tech
  'cerveau':              'brain',
  'robot':                'bot',
  'processeur':           'cpu',
  // Sécurité
  'bouclier':             'shield',
  'cadenas':              'lock',
  'cle':                  'key',
  'badge':                'badge',
  // Business
  'malette':              'briefcase',
  'mallette':             'briefcase',
  'fichier':              'file',
  'document':             'file',
  'recu':                 'receipt',
  'pressepapier':         'clipboard',
  'dossier':              'folder',
  // Équipe
  'utilisateurs':         'users',
  'utilisateur':          'user',
  'utilisateurvalide':    'usercheck',
  // Support
  'telephone':            'phone',
  'email':                'mail',
  'message':              'message',
  'casque':               'headphones',
  'bouee':                'lifebuoy',
  // Temps
  'calendrier':           'calendar',
  'horloge':              'clock',
  'cloche':               'bell',
  // Analytics
  'tendance':             'trendup',
  'graphbarre':           'barchart',
  'graphcamembert':       'piechart',
  'graphligne':           'linechart',
  // Web
  'globe':                'globe',
  'nuage':                'cloud',
  'wifi':                 'wifi',
  // Finance
  'carte':                'card',
  'portefeuille':         'wallet',
  'euro':                 'euro',
  // Produit
  'colis':                'package',
  'cadeau':               'gift',
  'etiquette':            'tag',
  'camion':               'truck',
  // Réglages
  'reglages':             'settings',
  'cle12':                'wrench',
  'engrenage':            'cog',
  // Validation
  'coche':                'check',
  'check':                'check',
  'pouce':                'thumbsup',
  'coeur':                'heart',
  // Médias
  'camera':               'camera',
  'photo':                'image',
  'image':                'image',
  'video':                'video',
  // Lieux
  'batiment':             'building',
  'maison':               'home',
  // Misc
  'oeil':                 'eye',
  // Aucune
  'aucune':               null,
  'aucun':                null,
};

function resolveIcon(rawValue) {
  if (!rawValue || typeof rawValue !== 'string') return null;

  // 1) Lookup direct par clé technique (ex: "sparkles", "star", "rocket")
  if (ICON_BY_KEY[rawValue]) return ICON_BY_KEY[rawValue];

  // 2) Lookup par nom Lucide direct (ex: "Sparkles", "Star", "Wand2")
  if (Icons[rawValue]) return Icons[rawValue];

  // 3) Lookup par label FR (ex: "Étoiles IA", "Couronne", "Fusée")
  //    → normalisation : retire accents + espaces, lowercase
  const norm = rawValue
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')   // strip combining diacritics (accents)
    .replace(/[\s\-_]+/g, '')          // strip spaces, dashes, underscores
    .toLowerCase();

  const mappedKey = FR_LABEL_TO_KEY[norm];
  if (mappedKey === null) return null;            // "aucune" → pas d'icône
  if (mappedKey && ICON_BY_KEY[mappedKey]) return ICON_BY_KEY[mappedKey];

  return null;  // fallback final → composant l'utilisateur retombera sur Check
}

// Rendu d'un "Détail en plus" — aligné avec PacksWidget.jsx (page /tarifs).
// Supporte 2 modes :
//   - HIGHLIGHTED (cadre orange) : icône + titre dans un bandeau, descriptions
//     en dessous indentées (sans icône).
//   - NORMAL (texte simple) : titre seul + chaque description préfixée par
//     l'icône choisie (fallback Check vert).
function ExtraDetailItem({ detail }) {
  const Icon = resolveIcon(detail?.icon) || Check;
  const highlighted = !!detail?.highlighted;
  // Supporte les 2 structures : detail.descriptions (array) ou detail.detail (string)
  const descriptions = Array.isArray(detail?.descriptions)
    ? detail.descriptions.filter(Boolean)
    : (detail?.detail ? [detail.detail] : []);

  if (highlighted) {
    return (
      <li style={{
        listStyle: 'none',
        padding: '12px',
        background: 'rgba(226, 114, 40, 0.08)',
        border: '1px solid rgba(226, 114, 40, 0.3)',
        borderRadius: 8,
      }}>
        {detail?.title && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: descriptions.length ? 6 : 0 }}>
            <Icon size={16} color="var(--color-primary, #e27228)" style={{ flexShrink: 0 }} />
            <strong style={{ fontWeight: 700, color: 'var(--color-text, #0f172a)', fontSize: '14px' }}>
              {detail.title}
            </strong>
          </div>
        )}
        {descriptions.map((desc, idx) => (
          <div key={idx} style={{
            color: 'var(--color-text-muted, #64748b)',
            fontSize: '13px',
            lineHeight: 1.5,
            fontWeight: 400,
            paddingLeft: 24,
          }}>
            {desc}
          </div>
        ))}
      </li>
    );
  }

  // Bloc normal : titre (optionnel) + descriptions avec icône
  return (
    <li style={{ listStyle: 'none', fontSize: '14px' }}>
      {detail?.title && (
        <div style={{ fontWeight: 700, color: 'var(--color-text, #0f172a)', fontSize: '14px', marginBottom: descriptions.length ? 6 : 0 }}>
          {detail.title}
        </div>
      )}
      {descriptions.map((desc, idx) => (
        <div key={idx} style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '8px',
          color: 'var(--color-text, #0f172a)',
          fontSize: '13px',
          lineHeight: 1.5,
          fontWeight: 400,
          marginTop: idx === 0 ? 0 : '4px',
        }}>
          <Icon size={14} color="var(--color-primary, #e27228)" style={{ flexShrink: 0, marginTop: '3px' }} />
          <span>{desc}</span>
        </div>
      ))}
    </li>
  );
}

export default function PacksPreviewBlock({ config = {}, packs = [] }) {
  const showToggle = config.show_billing_toggle !== false;
  const defaultBilling = config.default_billing === 'yearly' ? 'yearly' : 'monthly';
  const [billing, setBilling] = useState(defaultBilling);

  const maxFeatures = Math.max(1, Math.min(50, parseInt(config.max_features_per_pack, 10) || 5));
  const seeMoreText = config.see_more_text || 'Voir tous les détails';
  const seeMoreUrl = config.see_more_url || '/tarifs';

  // Filtre par pack_ids si specifies, sinon tous les packs visibles sur le site
  const allActive = (packs || []).filter(p => p?.active_on_site !== false && p?.is_active !== false);
  const ids = Array.isArray(config.pack_ids) ? config.pack_ids.filter(Boolean) : [];
  const visiblePacks = ids.length > 0
    ? allActive.filter(p => ids.includes(p.id))
    : allActive;

  if (visiblePacks.length === 0) return null;

  return (
    <section style={{
      padding: '44px 0',
      background: 'var(--color-bg, #fff)',
    }}>
      <div className="container-page">
        {/* Titre + sous-titre */}
        {(config.title || config.subtitle) && (
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            {config.title && (
              <h2 style={{
                fontSize: 'clamp(28px, 4vw, 40px)',
                fontWeight: 800, lineHeight: 1.15, letterSpacing: '-0.02em',
                margin: '0 0 14px', color: 'var(--color-text, #0f172a)',
              }}>{config.title}</h2>
            )}
            {config.subtitle && (
              <p style={{
                fontSize: 17, color: 'var(--color-text-muted, #64748b)',
                margin: 0, maxWidth: 640, marginLeft: 'auto', marginRight: 'auto',
                lineHeight: 1.6,
              }}>{config.subtitle}</p>
            )}
          </div>
        )}

        {/* Toggle Mensuel / Annuel */}
        {showToggle && (
          <div style={{
            display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 32,
          }}>
            <ToggleBtn active={billing === 'monthly'} onClick={() => setBilling('monthly')}>
              Paiement mensuel
            </ToggleBtn>
            <ToggleBtn active={billing === 'yearly'} onClick={() => setBilling('yearly')}>
              Paiement annuel
            </ToggleBtn>
          </div>
        )}

        {/* Grille de cartes packs — class packs-preview-grid pour la regle CSS
            mobile (1 colonne en < 768px) dans globals.css */}
        <div className="packs-preview-grid" style={{
          display: 'grid',
          gridTemplateColumns: `repeat(auto-fit, minmax(260px, 1fr))`,
          gap: 22,
          alignItems: 'stretch',
          maxWidth: 1200, margin: '0 auto',
        }}>
          {visiblePacks.map(pack => (
            <PackCard
              key={pack.id}
              pack={pack}
              billing={billing}
              maxFeatures={maxFeatures}
            />
          ))}
        </div>

        {/* Bouton "Voir tous les details" */}
        {seeMoreUrl && (
          <div style={{ textAlign: 'center', marginTop: 36 }}>
            <a href={seeMoreUrl} style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '12px 24px',
              fontSize: 15, fontWeight: 600,
              color: 'var(--color-primary, #e27228)',
              background: 'transparent',
              border: '1.5px solid var(--color-primary, #e27228)',
              borderRadius: 999,
              textDecoration: 'none',
              transition: 'background 0.15s, color 0.15s',
            }}>
              {seeMoreText}
              <ArrowRight size={16} />
            </a>
          </div>
        )}
      </div>
    </section>
  );
}

function ToggleBtn({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '10px 22px',
        background: active ? 'var(--color-primary, #e27228)' : '#fff',
        color: active ? 'var(--color-primary-text, #fff)' : 'var(--color-text, #0f172a)',
        border: active ? '1px solid var(--color-primary, #e27228)' : '1px solid var(--color-border, #e2e8f0)',
        borderRadius: 999,
        fontSize: 13, fontWeight: 600,
        cursor: 'pointer',
        boxShadow: active ? '0 2px 6px rgba(226,114,40,0.25)' : 'none',
        transition: 'all 0.15s',
      }}
    >
      {children}
    </button>
  );
}

function PackCard({ pack, billing, maxFeatures }) {
  const isYearly = billing === 'yearly';
  const monthly = Number(pack.price_monthly) || 0;
  const yearly = Number(pack.price_yearly) || 0;
  const yearlyPerMonth = yearly > 0 ? yearly / 12 : monthly;
  const displayPrice = isYearly
    ? (yearlyPerMonth === 0 ? '0' : formatPrice(yearlyPerMonth))
    : (monthly === 0 ? '0' : formatPrice(monthly));
  const priceSubtitle = isYearly
    ? (yearlyPerMonth === 0 ? 'Gratuit, pour toujours' : '€ HTVA / mois (annuel)')
    : (monthly === 0 ? 'Gratuit, pour toujours' : '€ HTVA / mois');
  // V1.25.956 — Calcul economie annuelle vs mensuel : si l'admin a saisi un
  // price_yearly inferieur a (price_monthly * 12), on affiche le delta en
  // badge vert quand le toggle est sur "annuel". 0 si pas d'economie ou pack
  // gratuit. Arrondi a l'entier (les centimes ne sont pas pertinents pour un
  // gain commercial).
  const yearlyMonths = monthly * 12;
  const yearlySaving = (isYearly && monthly > 0 && yearly > 0 && yearly < yearlyMonths)
    ? Math.round(yearlyMonths - yearly)
    : 0;

  // Features = extra_details visibles (max N premieres)
  // V1.25.1026 — Supporte la structure RICHE (title + icon + descriptions[] +
  // highlighted) en plus de la structure SIMPLE legacy (detail string seul).
  // Le filtre garde tout détail qui a soit un .detail, soit un .title, soit
  // des .descriptions (sinon c'est vide on saute).
  const allFeatures = (pack.extra_details || [])
    .filter(d => d && d.visible !== false && (
      d.detail ||
      d.title ||
      (Array.isArray(d.descriptions) && d.descriptions.some(Boolean))
    ));

  // Séparer les blocs HIGHLIGHTED (cadre orange "EN AVANT", ex: Agent Ligobox)
  // des blocs normaux. Les highlighted sont rendus EN HAUT (juste après le prix
  // et le badge "Vous économisez"), les normaux en dessous.
  // La limite "max features" s'applique UNIQUEMENT aux features normales : le(s)
  // bloc(s) mis en avant sont toujours affichés et ne consomment pas le quota.
  const highlightedFeatures = allFeatures.filter(f => f && f.highlighted);
  const normalFeatures      = allFeatures.filter(f => !f || !f.highlighted).slice(0, maxFeatures);

  // Highlight si POPULAIRE / TOUT INCLUS / etc. (depuis le metadata du pack)
  const isHighlight = pack.is_default_for_signup === false &&
    (pack.name || '').toLowerCase().includes('expert');

  // V1.25.952 — CTA URL : pointe vers /signup pour creer un compte. Le user
  // s'inscrit, le pack par defaut Starter (0€) est attribue automatiquement,
  // puis il est redirige vers /admin/packs ou il pourra choisir/payer le pack
  // qui l'interesse vraiment. Le slug est passe en query pour informer la page
  // /signup du pack souhaite (optionnel, pour analytics ou pre-selection).
  const ctaUrl = pack.slug
    ? `https://app.ligobox.com/signup?pack=${pack.slug}`
    : `https://app.ligobox.com/signup`;

  return (
    <div style={{
      position: 'relative',
      display: 'flex', flexDirection: 'column',
      background: '#fff',
      border: isHighlight ? '2px solid var(--color-primary, #e27228)' : '1px solid var(--color-border, #e2e8f0)',
      borderRadius: 16,
      padding: '28px 24px 24px',
      boxShadow: isHighlight
        ? '0 12px 32px rgba(226, 114, 40, 0.18)'
        : '0 2px 8px rgba(15, 23, 42, 0.04)',
      transition: 'transform 0.2s, box-shadow 0.2s',
    }}>
      {isHighlight && (
        <span style={{
          position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
          padding: '4px 12px', borderRadius: 999,
          background: 'var(--color-primary, #e27228)',
          color: 'var(--color-primary-text, #fff)',
          fontSize: 11, fontWeight: 700, letterSpacing: '0.05em',
        }}>POPULAIRE</span>
      )}

      {/* Nom + description courte */}
      <div style={{ marginBottom: 16 }}>
        <h3 style={{
          fontSize: 22, fontWeight: 800, margin: '0 0 6px',
          color: 'var(--color-text, #0f172a)',
        }}>{pack.name}</h3>
        {pack.description && (
          <p style={{
            fontSize: 13, lineHeight: 1.5,
            color: 'var(--color-text-muted, #64748b)',
            margin: 0,
            minHeight: 38,
          }}>{pack.description || ''}</p>
        )}
      </div>

      {/* Prix */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
          <span style={{
            fontSize: 38, fontWeight: 800, lineHeight: 1,
            color: isHighlight ? 'var(--color-primary, #e27228)' : 'var(--color-text, #0f172a)',
          }}>{displayPrice}</span>
          {displayPrice !== '0' && (
            <span style={{
              fontSize: 15, fontWeight: 600,
              color: 'var(--color-text-muted, #64748b)',
            }}>€</span>
          )}
        </div>
        <p style={{
          fontSize: 12, color: 'var(--color-text-muted, #64748b)',
          margin: '4px 0 0',
        }}>{priceSubtitle}</p>
        {/* V1.25.956 — Badge "Vous economisez XX €" affiche uniquement en mode
            annuel ET si une economie reelle existe (price_yearly < price_monthly*12).
            Style vert pastel arrondi inspire des concurrents. */}
        {yearlySaving > 0 && (
          <div style={{
            display: 'inline-block',
            marginTop: 8,
            padding: '4px 10px',
            background: '#d1fae5',
            color: '#065f46',
            fontSize: 12, fontWeight: 700,
            borderRadius: 999,
            border: '1px solid #a7f3d0',
          }}>
            Vous économisez {yearlySaving} €
          </div>
        )}
      </div>

      {/* Blocs HIGHLIGHTED (ex: "IA Ligobox" cadre orange "EN AVANT") —
          V1.25.1026 : remontés EN HAUT, juste après le badge "Vous économisez",
          AVANT la liste des features normales. Style concurrent.
      */}
      {highlightedFeatures.length > 0 && (
        <ul style={{
          listStyle: 'none', padding: 0, margin: '0 0 16px',
          display: 'flex', flexDirection: 'column', gap: 10,
        }}>
          {highlightedFeatures.map((f, i) => (
            <ExtraDetailItem key={`hl-${i}`} detail={f} />
          ))}
        </ul>
      )}

      {/* Liste features NORMALES — bullets simples + titres avec icône.
          Affichées APRES les blocs highlighted. */}
      {normalFeatures.length > 0 && (
        <ul style={{
          listStyle: 'none', padding: 0, margin: '0 0 22px',
          display: 'flex', flexDirection: 'column', gap: 10,
          flex: 1,
        }}>
          {normalFeatures.map((f, i) => (
            <ExtraDetailItem key={`n-${i}`} detail={f} />
          ))}
        </ul>
      )}

      {/* CTA */}
      <a href={ctaUrl} style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        padding: '12px 16px',
        fontSize: 14, fontWeight: 600,
        background: isHighlight ? 'var(--color-primary, #e27228)' : '#fff',
        color: isHighlight ? 'var(--color-primary-text, #fff)' : 'var(--color-primary, #e27228)',
        border: isHighlight ? 'none' : '1.5px solid var(--color-primary, #e27228)',
        borderRadius: 10,
        textDecoration: 'none',
        marginTop: 'auto',
      }}>
        Choisir {pack.name}
      </a>
    </div>
  );
}

function formatPrice(n) {
  if (n == null || isNaN(n)) return '0';
  const r = Math.round(n * 100) / 100;
  return Number.isInteger(r) ? String(r) : String(r).replace('.', ',');
}
